import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.reliabletouchservices.co.za/api/v1';
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

class ContactPoolAPI {
  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
    });
    this._initInterceptors();
    this.endpoints = {
      auth: {
        login: '/auth/token/',
        refresh: '/auth/token/refresh/',
        verify: '/auth/token/verify/',
        logout: '/auth/logout/',
        me: '/auth/me/',
        setupAdmin: '/auth/setup/create-admin/',
      },
      agents: {
        create: '/agents/create/',
        list: '/agents/list/',
        get: (id) => `/agents/${id}/`,
        update: (id) => `/agents/${id}/update/`,
        toggleStatus: (id) => `/agents/${id}/toggle-status/`,
        delete: (id) => `/agents/${id}/delete/`,
      },
      pool: {
        // Admin
        uploadExcel: '/pool/admin/upload-excel/',
        adminPool: '/pool/admin/pool/',
        adminClaims: '/pool/admin/claims/',
        adminDashboard: '/pool/admin/dashboard/',
        adminAwaradContacts: '/pool/admin/award-contacts/',
        adminUploadStats: '/pool/admin/upload-stats/',
        adminAgentPerformance: '/pool/admin/agent-performance/',
        adminAgentClaimsDetail: (agentId) => `/pool/admin/agent-performance/${agentId}/claims/`,
        adminSuccessLogs: '/pool/admin/success-logs/',
        toggleContactInquiry: (id) => `contact/toggle-contact-state/${id}/`,
        toggleAgentRequest: (id) =>`contact/toggle-agent-request-state/${id}/`,
        // Agent
        agentPool: '/pool/agent/pool/',
        agentClaim: '/pool/agent/claim/',
        agentMyClaims: '/pool/agent/my-claims/',
        agentClaimComplete: (id) => `/pool/agent/claim/${id}/complete/`,
        agentLogCall: '/pool/agent/log-call/',
        agentDashboard: '/pool/agent/dashboard/',
        agentMyPerformance: '/pool/agent/my-performance/',
      },
      contact: {
        create: '/contact/create/',
        connectAgentCreate: 'contact/connect-agent/create/',
        list: '/contact/list/',
        connectAgentList: '/contact/connect-agent/list/',
      },
    };
  }

  _initInterceptors() {
    this.instance.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    this.instance.interceptors.response.use(
      (res) => res,
      async (error) => {
        const orig = error.config;
        if (error.response?.status === 401 && !orig._retry) {
          orig._retry = true;
          try {
            const refresh = localStorage.getItem('refresh_token');
            if (!refresh) throw new Error('no refresh');
            const res = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, { refresh });
            localStorage.setItem('access_token', res.data.access);
            if (res.data.refresh) localStorage.setItem('refresh_token', res.data.refresh);
            orig.headers.Authorization = `Bearer ${res.data.access}`;
            return this.instance(orig);
          } catch {
            this.clearTokens();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setTokens(access, refresh) {
    localStorage.setItem('access_token', access);
    if (refresh) localStorage.setItem('refresh_token', refresh);
  }

  clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  isAuthenticated() { return !!localStorage.getItem('access_token'); }

  async login(email, password) {
    const res = await this.instance.post(this.endpoints.auth.login, { email, password });
    if (res.data.access) this.setTokens(res.data.access, res.data.refresh);
    return res.data;
  }

  async logout() {
    const refresh = localStorage.getItem('refresh_token');
    try { await this.instance.post(this.endpoints.auth.logout, { refresh }); } catch { }
    this.clearTokens();
  }

  async getMe() { return (await this.instance.get(this.endpoints.auth.me)).data; }

  async createAgent(data) { return (await this.instance.post(this.endpoints.agents.create, data)).data; }
  async listAgents(params = {}) { return (await this.instance.get(this.endpoints.agents.list, { params })).data; }
  async getAgent(id) { return (await this.instance.get(this.endpoints.agents.get(id))).data; }
  async updateAgent(id, data) { return (await this.instance.put(this.endpoints.agents.update(id), data)).data; }
  async toggleAgentStatus(id) { return (await this.instance.post(this.endpoints.agents.toggleStatus(id))).data; }
  async deleteAgent(id) { return (await this.instance.delete(this.endpoints.agents.delete(id))).data; }

  async uploadExcel(file) {
    const fd = new FormData();
    fd.append('file', file);
    return (await this.instance.post(this.endpoints.pool.uploadExcel, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  }

  async getAdminPool(params = {}) { return (await this.instance.get(this.endpoints.pool.adminPool, { params })).data; }
  async getAdminClaims(params = {}) { return (await this.instance.get(this.endpoints.pool.adminClaims, { params })).data; }
  async getAdminDashboard() { return (await this.instance.get(this.endpoints.pool.adminDashboard)).data; }
  async getAdminUploadStats(params = {}) { return (await this.instance.get(this.endpoints.pool.adminUploadStats, { params })).data; }
  async getAdminAgentPerformance(params = {}) { return (await this.instance.get(this.endpoints.pool.adminAgentPerformance, { params })).data; }
  async getAdminAgentClaimsDetail(agentId, params = {}) { return (await this.instance.get(this.endpoints.pool.adminAgentClaimsDetail(agentId), { params })).data; }
  async getAdminSuccessLogs(params = {}) { return (await this.instance.get(this.endpoints.pool.adminSuccessLogs, { params })).data; }
  async AwardContacts(data) { return (await this.instance.post(this.endpoints.pool.adminAwaradContacts, data)).data; }

  async getAgentPool() { return (await this.instance.get(this.endpoints.pool.agentPool)).data; }
  async claimContacts(contactIds) { return (await this.instance.post(this.endpoints.pool.agentClaim, { contact_ids: contactIds })).data; }
  async getMyClaims() { return (await this.instance.get(this.endpoints.pool.agentMyClaims)).data; }
  async markClaimComplete(id) { return (await this.instance.post(this.endpoints.pool.agentClaimComplete(id))).data; }
  async logCall(data) { return (await this.instance.post(this.endpoints.pool.agentLogCall, data)).data; }
  async getAgentDashboard() { return (await this.instance.get(this.endpoints.pool.agentDashboard)).data; }
  async getAgentMyPerformance(params = {}) { return (await this.instance.get(this.endpoints.pool.agentMyPerformance, { params })).data; }

  async createContact(data) { return (await this.instance.post(this.endpoints.contact.create, data)).data; }
  async createConnectAgent(data) { return (await this.instance.post(this.endpoints.contact.connectAgentCreate, data)).data; }
  async getAdminInquiries(params = {}) { return (await this.instance.get(this.endpoints.contact.list, { params })).data; }
  async getAdminConnectRequests(params = {}) { return (await this.instance.get(this.endpoints.contact.connectAgentList, { params })).data; }
  async toggleContactInquiry(id) { return (await this.instance.post(this.endpoints.pool.toggleContactInquiry(id))); }
  async toggleAgentRequest(id) { return (await this.instance.post(this.endpoints.pool.toggleAgentRequest(id))); }
}

const api = new ContactPoolAPI();
export default api;
